import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MessageSquare, Send } from "lucide-react";
import RevealNav from "@/components/RevealNav";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll get back to you soon.");
    } catch (err) {
      const code = err?.response?.status;
      toast.error(
        code === 429
          ? "Too many messages. Please try again in a little while."
          : "Couldn't send your message. Please email support@ordrs.ai directly."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <RevealNav />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.pexels.com/photos/4350099/pexels-photo-4350099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt=""
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
        </div>
        <div className="ds-container pt-32 pb-20 lg:pt-40 lg:pb-24">
          <motion.div {...fade}>
            <p className="overline mb-6">get in touch</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl mb-6 max-w-3xl">
              Let's talk about<br />
              <span className="font-script text-primary text-5xl sm:text-7xl lg:text-[8rem] font-normal leading-[0.75] block mt-3">
                your business.
              </span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Questions about pricing, a custom setup, or want a live walkthrough? Send us a note —
              a real person reads every message, and we usually reply within a day.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── FORM ── */}
      <section className="ds-container pb-28 lg:pb-36">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* left: form */}
          <motion.div className="lg:col-span-7" {...fade}>
            <Card className="p-8 lg:p-10">
              {sent ? (
                <div className="py-12 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                    <Send className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Message sent.</h3>
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    Thanks for reaching out. We'll get back to you at <span className="text-foreground">{form.email}</span> soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6">
                  {/* honeypot — visually hidden, off accessibility tree */}
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={set("company")}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                  />
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name">Name</Label>
                      <Input id="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email">Email</Label>
                      <Input id="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@business.com" data-testid="contact-email" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-message">Message</Label>
                    <Textarea
                      id="contact-message"
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about your business and what you're looking for…"
                      rows={6}
                      data-testid="contact-message"
                    />
                  </div>
                  <Button type="submit" size="lg" className="h-12 px-8 text-base font-bold group" disabled={sending} data-testid="contact-submit">
                    {sending ? "Sending…" : (
                      <>Send message <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" /></>
                    )}
                  </Button>
                </form>
              )}
            </Card>
          </motion.div>

          {/* right: direct contact */}
          <motion.div className="lg:col-span-5" {...fade} transition={{ duration: 0.6, delay: 0.15 }}>
            <div className="space-y-4">
              <Card className="p-8">
                <Mail className="w-5 h-5 text-primary mb-4" />
                <h3 className="text-lg font-bold mb-1">Email us directly</h3>
                <p className="text-sm text-muted-foreground mb-3">Prefer your own inbox? We're here to help.</p>
                <a href="mailto:support@ordrs.ai" className="text-primary font-medium hover:underline">support@ordrs.ai</a>
              </Card>
              <Card className="p-8">
                <MessageSquare className="w-5 h-5 text-primary mb-4" />
                <h3 className="text-lg font-bold mb-1">Get ready to take ordrs</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  With one prompt, you can have a fully personalized business with built in receptionist instantly. 
                </p>
                <Link to="/#preview">
                  <Button variant="outline" size="sm" className="font-bold group">
                    Build custom business <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
